/** View state the HUD, the input and the world share: zoomed out or following, and the open panel. */
import type { Game } from '../game';
import type { Inset, Panel } from '../types';

export function setOverview(game: Game, overview: boolean): void {
  game.local.viewTarget = overview ? 1 : 0;
  if (game.store.get().overview !== overview) game.store.set({ overview });
}

export const toggleOverview = (game: Game): void => {
  setOverview(game, !game.store.get().overview);
};

/** A pinch or the wheel sets the zoom anywhere between following and the whole planet. */
export function setViewTarget(game: Game, target: number): void {
  const v = Math.max(0, Math.min(1, target));
  game.local.viewTarget = v;
  const overview = v > 0.5;
  if (game.store.get().overview !== overview) game.store.set({ overview });
}

/** The panels whose sheets leave the planet free: I walk and turn it while they're open. */
const FREE: ReadonlySet<Panel> = new Set<Panel>(['hub', 'build', 'info', 'load']);

export const isFree = (panel: Panel): boolean => FREE.has(panel);

/** Open a panel; one about a tile (`tile`) closes when I walk off that tile. */
export function openPanel(game: Game, panel: Panel, tile = -1): void {
  game.store.set({ panel, panelTile: tile });
}

/** A sheet reports the part of the screen it covers; the world keeps the planet clear of it. */
export function setInset(game: Game, inset: Inset): void {
  const was = game.store.get().inset;
  if (was.bottom !== inset.bottom || was.right !== inset.right) game.store.set({ inset });
}

export function closePanel(game: Game): void {
  const s = game.store.get();
  if (s.panel !== 'none' || s.panelTile >= 0) game.store.set({ panel: 'none', panelTile: -1 });
}

/** Whether the planet takes taps and keys right now: with no sheet open, or a free one. */
export function canWalk(game: Game): boolean {
  const s = game.store.get();
  return s.view !== null && (s.panel === 'none' || isFree(s.panel));
}

/** A panel about a tile (the hub's card, an extractor's info) closes when I walk off that tile. */
export function startTilePanels(game: Game): () => void {
  return game.store.subscribe((s) => {
    if (s.panelTile >= 0 && s.here !== s.panelTile) closePanel(game);
  });
}
